import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { FormikHelpers, FormikValues } from "formik";

import { UserDynamicMenuItemsPages, UserMenuItemsModel } from "../../UserMenuItems";
import { AssignUserRole, EditUser } from "../../../../services/authorization/Permissions";
import DisplayAddedBy from "../../../../components/data-components/DisplayAddedBy";
import DisplayDateAdded from "../../../../components/data-components/DisplayDateAdded";
import EditDescription from "../../../../components/data-components/EditDescription";
import {
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import PageLayout from "../../../_layout/PageLayout";
import {
  EditUserRole,
  EditEmploymentStatus,
  DisplayFirstName,
  DisplayLastName,
  DisplayPhone,
  DisplayUserName,
  DisplayEmail,
} from "../../components/DataComponents";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { UserEditInput } from "../../../../api-models/request/UserInput";
import Form from "../../../../components/form/Form";
import { EmploymentStatusesStorageModel } from "../../../../services/user/EmploymentStatusesStorage";
import { UserRolesStorageModel } from "../../../../services/user/UserRolesStorage";
import { api } from "../../../../services/api/ApiService";
import DisplayImage from "../../../../components/data-components/DisplayImage";
import { HeaderProfile } from "../../../../components/svg/svgImageList";
import { UserInViewStorageModel } from "../../../../services/user/UserInViewStorage";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { EmploymentStatus } from "../../../../services/user/EmploymentStatus";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import { CurrentOrganizationStorageModel } from "../../../../services/organization/CurrentOrganizationStorage";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const userInViewId = urlParamsService.getUserId()!;

  const [userInViewStorage] = useState(() =>
    UserInViewStorageModel.create({ userInViewId: userInViewId }, { api })
  );

  const [employmentStatusesStorage] = useState(() =>
    EmploymentStatusesStorageModel.create({}, { api })
  );

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [currentOrganizationStorage] = useState(() =>
    CurrentOrganizationStorageModel.create({}, { api })
  );

  const getLoadingState = useCallback(() => {
    return (
      !userInViewStorage.userInView.id ||
      employmentStatusesStorage.isLoading ||
      userRolesStorage.isLoading
    );
  }, [
    employmentStatusesStorage.isLoading,
    userInViewStorage.userInView.id,
    userRolesStorage.isLoading,
  ]);

  const menuItems = useMemo(() => {
    const userMenuItems = UserMenuItemsModel.create({
      userId: userInViewId,
      isLoading: getLoadingState(),
      editMode: true,
      dynamicMenuItemPage: UserDynamicMenuItemsPages.EditUser,
      organizationHasStripeCustomer: currentOrganizationStorage.organization.hasStripeCustomer,
    });

    return {
      staticItems: userMenuItems.getStaticItems(),
    };
  }, [currentOrganizationStorage.organization.hasStripeCustomer, getLoadingState, userInViewId]);

  const goToUsersList = useCallback(
    (userId: number) => history.push(`/users/${userId}`),
    [history]
  );

  const initialValues = useMemo(
    () => ({
      role: userInViewStorage.userInView.role.id,
      employmentStatus: userInViewStorage.userInView.employmentStatus,
      description: userInViewStorage.userInView.comments,
    }),
    [
      userInViewStorage.userInView.comments,
      userInViewStorage.userInView.employmentStatus,
      userInViewStorage.userInView.role.id,
    ]
  );

  const onFormCancel = useCallback(
    () => goToUsersList(userInViewId),
    [goToUsersList, userInViewId]
  );

  const updateUserDetails = useCallback(
    async (values: FormikValues) => {
      const user: UserEditInput = {
        id: userInViewId,
        employmentStatusId: values.employmentStatus.id,
        roleId: Number(values.role),
        comments: values.description,
      };

      await userInViewStorage.editUserDetails(user);
    },
    [userInViewId, userInViewStorage]
  );

  const handleSubmit = async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
    try {
      await updateUserDetails(values);
      warningLeavePageService.setIsDirty(false);
      return goToUsersList(userInViewId);
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, formActions);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  const onChangeEmploymentStatus = useCallback(
    (employmentStatusType: EmploymentStatus, setFieldValue: Function) => {
      if (employmentStatusType.isDefault) {
        userRolesStorage.setIsDefaultEmployeeStatus(true);
        return;
      }
      userRolesStorage.setIsDefaultEmployeeStatus(false);
      setFieldValue("role", userRolesStorage.rolesForCurrentEmploymentStatus[0].id);
    },
    [userRolesStorage]
  );

  useEffect(() => {
    if (
      userInViewStorage.userInView.id &&
      userInViewStorage.userInView.employmentStatus.isDefault
    ) {
      userRolesStorage.setIsDefaultEmployeeStatus(true);
    }
  }, [
    userInViewStorage.userInView.employmentStatus.isDefault,
    userInViewStorage.userInView.id,
    userRolesStorage,
  ]);

  return (
    <PageLayout
      accessWithAllOfThePermissions={[EditUser, AssignUserRole]}
      titleKey={userInViewStorage.userInView.name ? "ViewUser.title" : "ViewUser.title-no-username"}
      titleContext={{ userName: userInViewStorage.userInView.userName }}
      id="view-user-details"
      menuItems={menuItems}
      isLoading={getLoadingState()}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        onCancel={onFormCancel}
      >
        {({ values, setFieldValue }) => (
          <>
            <DataSection labelKey="EditUser.details">
              <SubSection>
                {userInViewStorage.hasImageUploaded ? (
                  <DisplayImage id="user-image" url={userInViewStorage.imageUrl} />
                ) : (
                  <DisplayImage id="user-image" placeholderImage={HeaderProfile} />
                )}
              </SubSection>

              <SubSection>
                <DisplayEmail email={userInViewStorage.userInView.email} />
                <EditEmploymentStatus
                  employmentStatus={values.employmentStatus}
                  setFieldValue={setFieldValue}
                  options={employmentStatusesStorage.employmentStatuses}
                  onChange={(employmentStatusType: EmploymentStatus) => {
                    onChangeEmploymentStatus(employmentStatusType, setFieldValue);
                  }}
                  organizationName={userInViewStorage.userInView.currentOrganization?.name}
                />
                <EditUserRole
                  role={values.role}
                  roleOptions={userRolesStorage.rolesForCurrentEmploymentStatus}
                  setFieldValue={setFieldValue}
                />
              </SubSection>

              <SubSection>
                <DisplayUserName userName={userInViewStorage.userInView.userName} />
                <DisplayFirstName name={userInViewStorage.userInView.firstName} />
                <DisplayLastName name={userInViewStorage.userInView.lastName} />
                <DisplayPhone phone={userInViewStorage.userInView.phone} />
              </SubSection>

              <SubSection>
                <DisplayDateAdded date={userInViewStorage.userInView.invitedToOrganizationDate} />
                <DisplayAddedBy
                  addedBy={userInViewStorage.userInView.invitedToOrganizationBy?.name}
                />
              </SubSection>
            </DataSection>

            <DataSection labelKey="general.comments">
              <EditDescription
                description={values.description}
                labelKey="general.comments"
                placeholderKey="EditComment.label"
              />
            </DataSection>
          </>
        )}
      </Form>
    </PageLayout>
  );
});
